<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import ConfirmationModal from '../../modal/ConfirmationModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ConfirmationModal,
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'update:show'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    targetConversationId: {
      required,
    },
  },
  data() {
    return {
      targetConversationId: null,
      isMerging: false,
    };
  },
  computed: {
    ...mapGetters({
      getConversationById: 'getConversationById',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    conversations() {
      return this.conversationIds
        .map(id => this.getConversationById(id))
        .filter(Boolean);
    },
    sourceConversations() {
      return this.conversations.filter(c => c.id !== this.targetConversationId);
    },
    targetConversation() {
      if (!this.targetConversationId) return null;
      return this.conversations.find(c => c.id === this.targetConversationId);
    },
  },
  methods: {
    onClose() {
      this.targetConversationId = null;
      this.v$.$reset();
      this.$emit('close');
    },
    selectTarget(conversationId) {
      this.targetConversationId = conversationId;
    },
    conversationLabel(conversation) {
      const name = conversation.meta?.sender?.name || 'N/A';
      return `#${conversation.id} - ${name}`;
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      const result = await this.$refs.confirmationModal.showConfirmation();
      if (!result) return;

      this.isMerging = true;
      try {
        const target = this.targetConversation;
        const sources = this.sourceConversations;
        await this.$store.dispatch('batchMergeConversations', {
          targetConversationDisplayId: target.id,
          sourceConversationDisplayIds: sources.map(c => c.id),
        });
        this.$store.dispatch('bulkActions/clearSelectedConversationIds');
        this.onClose();
        useAlert(this.$t('BULK_ACTION.MERGE.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('BULK_ACTION.MERGE.ERROR_MESSAGE'));
      } finally {
        this.isMerging = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" @close="onClose">
    <woot-modal-header
      :header-title="$t('BULK_ACTION.MERGE.TITLE')"
      :header-content="$t('BULK_ACTION.MERGE.DESCRIPTION')"
    />

    <form class="flex flex-col gap-4 px-8 py-4" @submit.prevent="onSubmit">
      <div v-if="targetConversation" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('BULK_ACTION.MERGE.TARGET_LABEL') }}
        </label>
        <div
          class="flex items-center gap-3 p-3 border rounded-lg border-n-teal-7 bg-n-teal-2"
        >
          <div class="i-lucide-merge text-n-teal-11 size-4 shrink-0" />
          <span class="text-sm font-medium text-n-teal-11">
            {{ conversationLabel(targetConversation) }}
          </span>
          <NextButton
            class="ml-auto"
            xs
            faded
            slate
            icon="i-lucide-x"
            @click.prevent="targetConversationId = null"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            targetConversation
              ? $t('BULK_ACTION.MERGE.SOURCES_LABEL')
              : $t('BULK_ACTION.MERGE.SELECT_TARGET')
          }}
        </label>
        <div class="flex flex-col gap-1 max-h-60 overflow-y-auto">
          <div
            v-for="conversation in targetConversation
              ? sourceConversations
              : conversations"
            :key="conversation.id"
            class="flex items-center gap-3 p-3 border rounded-lg transition-colors"
            :class="
              !targetConversation
                ? 'border-n-strong hover:border-n-teal-7 hover:bg-n-teal-2 cursor-pointer'
                : 'border-n-strong bg-n-alpha-2'
            "
            @click="!targetConversation && selectTarget(conversation.id)"
          >
            <div
              v-if="!targetConversation"
              class="i-lucide-circle-dot text-n-slate-9 size-4 shrink-0"
            />
            <div
              v-else
              class="i-lucide-arrow-right text-n-slate-9 size-4 shrink-0"
            />
            <span class="text-sm text-n-slate-12">
              {{ conversationLabel(conversation) }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="targetConversation"
        class="p-3 border rounded-lg bg-n-amber-1 border-n-amber-6 text-n-amber-11"
      >
        <p class="text-sm">
          {{ $t('BULK_ACTION.MERGE.WARNING') }}
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('BULK_ACTION.MERGE.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :disabled="!targetConversation"
          :is-loading="isMerging"
          :label="$t('BULK_ACTION.MERGE.SUBMIT')"
        />
      </div>
    </form>
    <ConfirmationModal
      ref="confirmationModal"
      :title="$t('BULK_ACTION.MERGE.CONFIRM.TITLE')"
      :description="
        $t('BULK_ACTION.MERGE.CONFIRM.DESCRIPTION', {
          sourceCount: sourceConversations.length,
          targetId: targetConversation?.id,
        })
      "
      :confirm-label="$t('BULK_ACTION.MERGE.CONFIRM.YES')"
      :cancel-label="$t('BULK_ACTION.MERGE.CONFIRM.CANCEL')"
    />
  </woot-modal>
</template>
